import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CityOption,
  loadCityOptions,
  splitCityCountry,
} from "../../utils/cityAsyncSelectOptions";
import {
  SIGNUP_OBJECTIVES_OPTIONS,
  SIGNUP_SECTOR_OPTIONS,
  SIGNUP_SEGMENT_OPTIONS,
  SignupProfileFormValues,
} from "./signupProfileConstants";
import {
  isAllowedExplorerSegment,
  normalizeExplorerSegment,
  stageLabelForExplorerSegment,
} from "../../explorer/explorerSegments";
import { getSignupProfileSelectStyles } from "./signupProfileSelectStyles";
import {
  EVENT_TERMS_CLOSING,
  EVENT_TERMS_INTRO,
  EVENT_TERMS_SECTIONS,
} from "./eventRegistrationTerms";

type SignupProfileStepProps = {
  values: SignupProfileFormValues;
  onChange: (patch: Partial<SignupProfileFormValues>) => void;
  showFieldErrors: boolean;
};

const menuPortalTarget = typeof document !== "undefined" ? document.body : undefined;

const PHONE_VALIDATION_DEBOUNCE_MS = 500;

function SignupProfileStep({ values, onChange, showFieldErrors }: SignupProfileStepProps) {
  const { t } = useTranslation();
  const termsContentRef = useRef<HTMLDivElement>(null);
  const [termsRead, setTermsRead] = useState(false);
  const [debouncedPhoneInvalid, setDebouncedPhoneInvalid] = useState(false);

  const checkTermsScrolledToBottom = useCallback(() => {
    const el = termsContentRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight <= clientHeight + 1 || scrollTop + clientHeight >= scrollHeight - 5) {
      setTermsRead(true);
    }
  }, []);

  useEffect(() => {
    checkTermsScrolledToBottom();
  }, [checkTermsScrolledToBottom]);

  useEffect(() => {
    const phone = values.phoneNumber.trim();
    if (!phone) {
      setDebouncedPhoneInvalid(false);
      return;
    }

    setDebouncedPhoneInvalid(false);
    const timer = window.setTimeout(() => {
      setDebouncedPhoneInvalid(!isValidPhoneNumber(phone));
    }, PHONE_VALIDATION_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [values.phoneNumber]);

  const phoneEmpty = !values.phoneNumber.trim();
  const phoneInvalid =
    debouncedPhoneInvalid || (showFieldErrors && (phoneEmpty || !isValidPhoneNumber(values.phoneNumber)));

  const fieldInvalid = useMemo(() => {
    if (!showFieldErrors) {
      return {
        phone: phoneInvalid,
        city: false,
        jobTitle: false,
        companyName: false,
        sector: false,
        segment: false,
        objectives: false,
        sectorsOfInterest: false,
        photoSharingConsent: false,
        termsAttachmentConsent: false,
        gdprConsent: false,
        marketingConsent: false,
      };
    }
    return {
      phone: phoneInvalid,
      city: !values.city.trim(),
      jobTitle: !values.jobTitle.trim(),
      companyName: !values.companyName.trim(),
      sector: !values.sector.trim(),
      segment: !isAllowedExplorerSegment(values.explorerAttendeeType),
      objectives: !values.objectivesForAttending.trim(),
      sectorsOfInterest: values.sectorsOfInterest.length === 0,
      photoSharingConsent: !values.photoSharingConsent,
      termsAttachmentConsent: !values.termsAttachmentConsent,
      gdprConsent: !values.gdprConsent,
      marketingConsent: !values.marketingConsent,
    };
  }, [phoneInvalid, showFieldErrors, values]);

  const citySelectStyles = useMemo(
    () => getSignupProfileSelectStyles<CityOption, false>(fieldInvalid.city),
    [fieldInvalid.city],
  );

  const singleSelectStyles = useMemo(
    () => getSignupProfileSelectStyles<{ value: string; label: string }, false>(),
    [],
  );

  const multiSelectStyles = useMemo(
    () => getSignupProfileSelectStyles<{ value: string; label: string }, true>(),
    [],
  );

  const sectorOptions = useMemo(
    () => SIGNUP_SECTOR_OPTIONS.map((s) => ({ value: s, label: s })),
    [],
  );

  const selectedSectors = useMemo(
    () =>
      values.sectorsOfInterest.map((value) => ({
        value,
        label: value,
      })),
    [values.sectorsOfInterest],
  );

  const selectClass = (invalid: boolean, multi = false) =>
    `auth-signup-react-select${multi ? " auth-signup-react-select--multi" : ""}${
      invalid ? " is-invalid-select" : ""
    }`;

  return (
    <div className="auth-signup-profile-form">
      <section className="auth-signup-profile-section">
        <h2 className="auth-signup-section-title">{t("explorer.signup.tellUs")}</h2>
        <div className="auth-signup-profile-panel">
          <div className="row g-3 g-lg-4">
            <div className="col-md-6">
              <label className="form-label small fw-semibold text-secondary mb-1">
                {t("explorer.signup.number")}
              </label>
              <div
                className={`form-control auth-signup-field auth-signup-phone-field${
                  fieldInvalid.phone ? " is-invalid" : ""
                }`}
              >
                <PhoneInput
                  international
                  defaultCountry="GB"
                  value={values.phoneNumber || undefined}
                  onChange={(value) => onChange({ phoneNumber: value || "" })}
                  placeholder={t("explorer.signup.phonePlaceholder")}
                  className="phone-input w-100"
                />
              </div>
              {fieldInvalid.phone && (
                <div className="invalid-feedback d-block">
                  {phoneEmpty
                    ? t("explorer.signup.phoneRequired")
                    : t("explorer.signup.phoneInvalid")}
                </div>
              )}
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-semibold text-secondary mb-1">
                {t("explorer.signup.cityCountry")}
              </label>
              <div className={selectClass(fieldInvalid.city)}>
                <AsyncSelect<CityOption, false>
                  cacheOptions
                  defaultOptions
                  loadOptions={loadCityOptions}
                  menuPortalTarget={menuPortalTarget}
                  menuPosition="fixed"
                  classNamePrefix="react-select"
                  styles={citySelectStyles}
                  value={
                    values.city
                      ? {
                          value: values.city,
                          label: values.country
                            ? `${values.city}, ${values.country}`
                            : values.city,
                          city: values.city,
                          country: values.country,
                        }
                      : null
                  }
                  onChange={(option) => {
                    if (!option) {
                      onChange({ city: "", country: "" });
                      return;
                    }
                    const split = splitCityCountry(option.city, option.country);
                    onChange({ city: split.city, country: split.country });
                  }}
                  placeholder={t("explorer.signup.cityPlaceholder")}
                />
              </div>
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-semibold text-secondary mb-1">
                {t("explorer.signup.jobTitle")}
              </label>
              <input
                className={`form-control auth-signup-field${
                  fieldInvalid.jobTitle ? " is-invalid" : ""
                }`}
                value={values.jobTitle}
                onChange={(e) => onChange({ jobTitle: e.target.value })}
                placeholder={t("explorer.signup.jobTitlePlaceholder")}
                autoComplete="organization-title"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-semibold text-secondary mb-1">
                {t("explorer.signup.companyName")}
              </label>
              <input
                className={`form-control auth-signup-field${
                  fieldInvalid.companyName ? " is-invalid" : ""
                }`}
                value={values.companyName}
                onChange={(e) => onChange({ companyName: e.target.value })}
                placeholder={t("explorer.signup.companyPlaceholder")}
                autoComplete="organization"
              />
            </div>
            <div className="col-12">
              <label className="form-label small fw-semibold text-secondary mb-1">
                {t("explorer.signup.sector")}
              </label>
              <div className={selectClass(fieldInvalid.sector)}>
                <Select
                  options={sectorOptions}
                  value={values.sector ? { value: values.sector, label: values.sector } : null}
                  onChange={(option) => onChange({ sector: option?.value || "" })}
                  placeholder={t("explorer.signup.selectSector")}
                  classNamePrefix="react-select"
                  styles={singleSelectStyles}
                  menuPortalTarget={menuPortalTarget}
                  menuPosition="fixed"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="auth-signup-profile-section">
        <h2 className="auth-signup-section-title">{t("explorer.segmentation.aboutYou")}</h2>
        <div className="auth-signup-profile-panel">
          <div className="row g-3 g-lg-4">
            <div className="col-md-6">
              <label className="form-label small fw-semibold text-secondary mb-1">
                {t("explorer.segmentation.whichDescribesYou")}
              </label>
              <div className={selectClass(fieldInvalid.segment)}>
                <Select
                  options={[...SIGNUP_SEGMENT_OPTIONS]}
                  value={
                    values.explorerAttendeeType
                      ? SIGNUP_SEGMENT_OPTIONS.find(
                          (o) =>
                            o.value ===
                            (normalizeExplorerSegment(values.explorerAttendeeType) ||
                              values.explorerAttendeeType),
                        ) || null
                      : null
                  }
                  onChange={(option) =>
                    onChange({
                      explorerAttendeeType: option?.value || "",
                      stageOfBusiness: stageLabelForExplorerSegment(option?.value || ""),
                    })
                  }
                  placeholder={t("explorer.segmentation.selectOption")}
                  classNamePrefix="react-select"
                  styles={singleSelectStyles}
                  menuPortalTarget={menuPortalTarget}
                  menuPosition="fixed"
                />
              </div>
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-semibold text-secondary mb-1">
                {t("explorer.segmentation.objectivesForAttending")}
              </label>
              <div className={selectClass(fieldInvalid.objectives)}>
                <Select
                  options={[...SIGNUP_OBJECTIVES_OPTIONS]}
                  value={
                    values.objectivesForAttending
                      ? SIGNUP_OBJECTIVES_OPTIONS.find(
                          (o) => o.value === values.objectivesForAttending,
                        ) || null
                      : null
                  }
                  onChange={(option) => onChange({ objectivesForAttending: option?.value || "" })}
                  placeholder={t("explorer.segmentation.selectObjective")}
                  classNamePrefix="react-select"
                  styles={singleSelectStyles}
                  menuPortalTarget={menuPortalTarget}
                  menuPosition="fixed"
                />
              </div>
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-semibold text-secondary mb-1">
                {t("events.sectorsOfInterest")}
              </label>
              <div className={selectClass(fieldInvalid.sectorsOfInterest, true)}>
                <Select
                  isMulti
                  options={sectorOptions}
                  value={selectedSectors}
                  onChange={(options) =>
                    onChange({ sectorsOfInterest: (options || []).map((o) => o.value) })
                  }
                  placeholder={t("explorer.segmentation.selectSectors")}
                  classNamePrefix="react-select"
                  styles={multiSelectStyles}
                  menuPortalTarget={menuPortalTarget}
                  menuPosition="fixed"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="auth-signup-profile-section">
        <h2 className="auth-signup-section-title">{t("explorer.segmentation.termsConsent")}</h2>
        <div className="auth-signup-profile-panel">
          <p className="small text-secondary mb-3 mb-md-2">
            {t("explorer.segmentation.reviewTerms")}
          </p>
          <div
            className="auth-signup-terms-preview"
            role="region"
            aria-label={t("explorer.segmentation.termsAndConditions")}
            tabIndex={0}
          >
            <div
              ref={termsContentRef}
              className="auth-signup-terms-content"
              onScroll={checkTermsScrolledToBottom}
            >
              <p className="fw-bold mb-1 text-body">{t("footer.eventPolicy")}</p>
              <p className="fw-bold mb-3 text-body">{t("explorer.segmentation.effectiveDate")}</p>
              <p className="mb-3 mb-0">{EVENT_TERMS_INTRO}</p>
              {EVENT_TERMS_SECTIONS.map((section) => (
                <div key={section.title} className="mb-3">
                  <p className="fw-bold mb-1 mb-0 text-body">{section.title}</p>
                  <p className="mb-0">{section.body}</p>
                </div>
              ))}
              <p className="mb-3 mb-0">{EVENT_TERMS_CLOSING}</p>
              <p className="fw-bold mb-1 mb-0 text-body">{t("explorer.segmentation.smbfLtd")}</p>
              <p className="fw-bold mb-0 text-body">{t("explorer.segmentation.effectiveDate")}</p>
            </div>
          </div>

          <hr className="auth-signup-consent-divider border-secondary-subtle my-4 opacity-100" />

          <div
            className={`auth-signup-consent-row form-check mb-3${
              fieldInvalid.photoSharingConsent ? " auth-signup-consent-row--invalid" : ""
            }${!termsRead ? " auth-signup-consent-row--locked" : ""}`}
          >
            <input
              className={`form-check-input mt-1${
                fieldInvalid.photoSharingConsent ? " is-invalid" : ""
              }`}
              type="checkbox"
              id="signup-photo-consent"
              checked={values.photoSharingConsent}
              disabled={!termsRead}
              onChange={(e) => onChange({ photoSharingConsent: e.target.checked })}
              aria-invalid={fieldInvalid.photoSharingConsent}
            />
            <label
              className={`form-check-label small${!termsRead ? " text-muted" : ""}`}
              htmlFor="signup-photo-consent"
            >
              {t("explorer.segmentation.photoConsentText")}
            </label>
          </div>
          <div
            className={`auth-signup-consent-row form-check mb-0${
              fieldInvalid.termsAttachmentConsent ? " auth-signup-consent-row--invalid" : ""
            }${!termsRead ? " auth-signup-consent-row--locked" : ""}`}
          >
            <input
              className={`form-check-input mt-1${
                fieldInvalid.termsAttachmentConsent ? " is-invalid" : ""
              }`}
              type="checkbox"
              id="signup-terms-consent"
              checked={values.termsAttachmentConsent}
              disabled={!termsRead}
              onChange={(e) => onChange({ termsAttachmentConsent: e.target.checked })}
              aria-invalid={fieldInvalid.termsAttachmentConsent}
            />
            <label
              className={`form-check-label small${!termsRead ? " text-muted" : ""}`}
              htmlFor="signup-terms-consent"
            >
              {t("explorer.segmentation.agreeTerms")}
            </label>
          </div>
        </div>
      </section>

      <section className="auth-signup-profile-section">
        <h2 className="auth-signup-section-title">{t("explorer.signup.consents")}</h2>
        <div className="auth-signup-consent-panel">
          <div className="form-check">
            <input
              className={`form-check-input${fieldInvalid.gdprConsent ? " is-invalid" : ""}`}
              type="checkbox"
              id="signup-gdpr"
              checked={values.gdprConsent}
              onChange={(e) => onChange({ gdprConsent: e.target.checked })}
            />
            <label className="form-check-label small" htmlFor="signup-gdpr">
              {t("explorer.signup.gdprText")}
            </label>
          </div>
          <div className="form-check">
            <input
              className={`form-check-input${fieldInvalid.marketingConsent ? " is-invalid" : ""}`}
              type="checkbox"
              id="signup-marketing"
              checked={values.marketingConsent}
              onChange={(e) => onChange({ marketingConsent: e.target.checked })}
            />
            <label className="form-check-label small" htmlFor="signup-marketing">
              {t("explorer.signup.marketingText")}
            </label>
          </div>
        </div>
      </section>
    </div>
  );
}

export default SignupProfileStep;

export function isSignupProfileComplete(values: SignupProfileFormValues): boolean {
  const segment = normalizeExplorerSegment(values.explorerAttendeeType);
  const stage =
    values.stageOfBusiness.trim() || stageLabelForExplorerSegment(segment);
  return (
    values.phoneNumber.trim().length > 0 &&
    isValidPhoneNumber(values.phoneNumber) &&
    values.city.trim().length > 0 &&
    values.jobTitle.trim().length > 0 &&
    values.companyName.trim().length > 0 &&
    values.sector.trim().length > 0 &&
    isAllowedExplorerSegment(values.explorerAttendeeType) &&
    values.objectivesForAttending.trim().length > 0 &&
    stage.length > 0 &&
    values.sectorsOfInterest.length > 0 &&
    values.photoSharingConsent &&
    values.termsAttachmentConsent &&
    values.gdprConsent &&
    values.marketingConsent
  );
}
